import React, { useMemo } from 'react'
import PersonalInfoCard from '../../../components/PersonalInfoCard'
import { useGetUserById } from '../../../api/user/useGetUserById'
import { useParams } from 'react-router'
import { useSelector } from 'react-redux'
import { currentUserSelector } from '../../../store/selectors/userSelector'
import _ from 'lodash'
import { Dealership } from '../../../lib/types/Dealership'
import { Outlet } from 'react-router-dom'

export default function UserMyProfile(): JSX.Element {
  const currentUser = useSelector(currentUserSelector)
  const params = useParams()
  const { data: user } = useGetUserById(params?.user_id || currentUser?.id)

  const dealerships = useMemo(() => user?.dealerships, [user])
  const associatedAgenciesAndDealerships = useMemo(
    () =>
      _(dealerships)
        .groupBy('organization_id')
        .reduce(
          (
            acc: Array<{
              label: string
              value: number
              options: Array<{ value: number; label: string }>
            }>,
            cur,
          ) => {
            const organization_name = cur[0].organization_name ?? undefined
            const organization_id = cur[0].organization_id ?? undefined
            if (!organization_name || !organization_id) return acc
            return [
              ...acc,
              {
                label: organization_name,
                value: organization_id,
                options: cur
                  .map((dealership: Dealership) => ({
                    value: dealership.id,
                    label: dealership.dealership,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label)),
              },
            ]
          },
          [],
        )
        .sort((a: { label: string }, b: { label: string }) =>
          a.label.localeCompare(b.label),
        ),
    [dealerships],
  )

  return (
    <>
      <div className={'pb-5 font-Spartan'}>
        <div className={'flex flex-col gap-5'}>
          <h3 className={'font-bold text-blue-primary text-base'}>Personal Info</h3>
          <PersonalInfoCard user={user} />
        </div>
        <div className={'flex flex-col gap-5 pt-12'}>
          <h3 className={'font-bold text-blue-primary text-base'}>
            Associated Agencies and Dealerships
          </h3>
          <div className={'flex flex-col gap-5'}>
            {associatedAgenciesAndDealerships?.map(
              (agency: {
                label: string
                value: number
                options: Array<{ value: number; label: string }>
              }) => {
                return (
                  <div
                    key={agency.value}
                    className={'p-4 gap-3 flex flex-col rounded-md bg-cultured-gray'}>
                    <div className={'flex items-center gap-3 border-b-2 pb-3'}>
                      <span className={'font-bold text-sm'}>{agency.label}</span>
                      <span className={'bg-yellow-secondary p-1.5 font-bold text-xs'}>
                        Agency
                      </span>
                    </div>
                    <div className={'flex flex-col gap-3'}>
                      <span className={'font-medium text-xs text-web-gray'}>
                        Dealerships
                      </span>
                      <div
                        className={
                          'flex items-center justify-center grid grid-cols-4 gap-5'
                        }>
                        {agency?.options?.map(
                          (dealership: { value: number; label: string }) => {
                            return (
                              <span
                                className={
                                  'font-medium py-2 rounded-md text-center text-blue-primary text-xs bg-cultured-gray'
                                }
                                key={dealership.value}>
                                {dealership?.label}
                              </span>
                            )
                          },
                        )}
                      </div>
                    </div>
                  </div>
                )
              },
            )}
          </div>
        </div>
      </div>
      <Outlet />
    </>
  )
}
